import { useState, useRef, useEffect } from 'react';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value as string); });
    try {
      const res = await fetch('https://readdy.ai/api/form/d7b552eoim692ipjluqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setSubmitted(true); form.reset(); }
      else { setError('Something went wrong. Please try again.'); }
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const inputCls = "w-full h-12 px-4 text-sm border border-[#0A1F44]/15 rounded-xl text-[#0A1F44] placeholder-[#0A1F44]/35 focus:outline-none focus:border-[#0369A1] focus:shadow-[0_0_0_3px_rgba(3,105,161,0.12)] transition-all duration-200";
  const selectCls = "w-full h-12 px-4 text-sm border border-[#0A1F44]/15 rounded-xl text-[#0A1F44] focus:outline-none focus:border-[#0369A1] focus:shadow-[0_0_0_3px_rgba(3,105,161,0.12)] transition-all duration-200 bg-white cursor-pointer";

  return (
    <section id="contact" className="py-24 md:py-32 relative overflow-hidden"
      style={{ background: 'linear-gradient(160deg, #f0f9ff 0%, #ffffff 50%, rgba(3,105,161,0.04) 100%)' }}>
      <div
        className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(3,105,161,0.07) 0%, transparent 70%)' }}
      />

      <div ref={sectionRef} className="max-w-[1300px] mx-auto px-6 md:px-10">
        <div
          className="text-center mb-16"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
        >
          <span className="text-[#0369A1] text-sm font-semibold tracking-[3px] uppercase mb-4 block">Schedule a Session</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0A1F44] mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Book Your Demo
          </h2>
          <p className="text-[#0A1F44]/60 text-lg max-w-xl mx-auto">
            See our AI solutions in action. Schedule a personalized 30-minute demo with our experts.
          </p>
        </div>

        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s' }}
        >
          {/* Left: Demo Booking Form */}
          <div className="bg-white rounded-2xl p-5 sm:p-8 md:p-10" style={{ boxShadow: '0 8px 32px rgba(10,31,68,0.08)', border: '1px solid rgba(10,31,68,0.06)' }}>
            <h3 className="text-xl sm:text-2xl font-bold text-[#0A1F44] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Reserve Your Spot
            </h3>
            <p className="text-[#0A1F44]/60 text-sm mb-8">Fill out the form below and we&apos;ll confirm your demo session within 2 hours.</p>

            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: 'rgba(3,105,161,0.10)' }}>
                  <i className="ri-calendar-check-line text-[#0369A1] text-3xl w-8 h-8 flex items-center justify-center" />
                </div>
                <h4 className="text-xl font-semibold text-[#0A1F44] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>Demo Requested!</h4>
                <p className="text-[#0A1F44]/60">We&apos;ll send you a calendar invite shortly. Thank you!</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 text-[#0369A1] text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap">
                  Book another demo
                </button>
              </div>
            ) : (
              <form data-readdy-form id="nexaura-demo-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Full Name *</label>
                    <input name="fullName" type="text" required placeholder="John Doe" className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Work Email *</label>
                    <input name="email" type="email" required placeholder="john@company.com" className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Company Name</label>
                    <input name="company" type="text" placeholder="Acme Inc." className={inputCls} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Phone Number</label>
                    <input name="phone" type="tel" placeholder="+91 98765 43210" className={inputCls} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Preferred Date</label>
                    <input name="preferredDate" type="date" className={inputCls + " cursor-pointer"} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Preferred Time</label>
                    <select name="preferredTime" className={selectCls}>
                      <option value="">Select time slot</option>
                      <option value="morning">Morning (9 AM - 12 PM)</option>
                      <option value="afternoon">Afternoon (12 PM - 4 PM)</option>
                      <option value="evening">Evening (4 PM - 7 PM)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Service Interest *</label>
                  <select name="serviceInterest" required className={selectCls}>
                    <option value="">Select a service to demo</option>
                    <option>Web Design &amp; Development</option>
                    <option>Mobile App Development</option>
                    <option>WhatsApp &amp; Business Automation</option>
                    <option>Voice Bots &amp; AI Assistants</option>
                    <option>AI &amp; Machine Learning</option>
                    <option>Computer Vision</option>
                    <option>IoT &amp; Industrial Automation</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0A1F44]/60 uppercase tracking-wider block mb-2">Tell us about your project</label>
                  <textarea
                    name="projectDetails" rows={3} maxLength={500}
                    placeholder="What challenges are you looking to solve? Any specific features you'd like to see?"
                    className="w-full px-4 py-3 text-sm border border-[#0A1F44]/15 rounded-xl text-[#0A1F44] placeholder-[#0A1F44]/35 focus:outline-none focus:border-[#0369A1] focus:shadow-[0_0_0_3px_rgba(3,105,161,0.12)] transition-all duration-200 resize-y"
                  />
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
                <button
                  type="submit" disabled={submitting}
                  className="w-full py-4 bg-[#0369A1] text-white font-semibold text-[15px] rounded-xl cursor-pointer hover:bg-[#075985] hover:shadow-[0_8px_24px_rgba(3,105,161,0.4)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin w-5 h-5 flex items-center justify-center" />Booking...</>
                  ) : (
                    <><i className="ri-calendar-event-line w-5 h-5 flex items-center justify-center" />Book My Demo →</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right: Demo Info */}
          <div className="lg:pl-8 py-0 sm:py-4">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0A1F44] leading-tight mb-4 sm:mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              See AI in{' '}<span className="text-[#0369A1]">action.</span>
            </h3>
            <p className="text-[#0A1F44]/65 text-[15px] sm:text-[16px] leading-relaxed mb-6 sm:mb-10">
              Experience firsthand how our AI solutions can transform your business operations. Our demos are tailored to your industry and use case.
            </p>

            <div className="space-y-4 sm:space-y-5 mb-6 sm:mb-10">
              <h4 className="text-sm font-semibold text-[#0A1F44]/50 uppercase tracking-wider">What to expect</h4>
              {[
                { icon: 'ri-presentation-line', title: 'Live Product Walkthrough', desc: 'See features relevant to your needs' },
                { icon: 'ri-question-answer-line', title: 'Q&A with AI Experts', desc: 'Get answers to your technical questions' },
                { icon: 'ri-file-list-3-line', title: 'Custom Roadmap', desc: 'Receive a tailored implementation plan' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: 'rgba(3,105,161,0.10)' }}>
                    <i className={`${item.icon} text-[#0369A1] text-base w-5 h-5 flex items-center justify-center`} />
                  </div>
                  <div>
                    <div className="text-[#0A1F44] font-semibold text-[15px]">{item.title}</div>
                    <div className="text-[#0A1F44]/55 text-sm">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
              <h4 className="text-sm font-semibold text-[#0A1F44]/50 uppercase tracking-wider">Need immediate assistance?</h4>
              {[
                { icon: 'ri-mail-line', label: 'Email us', value: 'services@nexaurainfinity.com', href: 'mailto:services@nexaurainfinity.com' },
                { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '+91 9791005603', href: 'https://wa.me/919791005603' },
                { icon: 'ri-phone-line', label: 'Phone', value: '+91 9944444223', href: 'tel:+919944444223' },
              ].map((item) => (
                <a key={item.label} href={item.href} rel="nofollow" className="flex items-center gap-3 text-[#0A1F44] hover:text-[#0369A1] transition-colors duration-200 cursor-pointer">
                  <i className={`${item.icon} text-[#0369A1] w-5 h-5 flex items-center justify-center`} />
                  <span className="text-sm font-medium">{item.value}</span>
                </a>
              ))}
            </div>


          </div>
        </div>
      </div>
    </section>
  );
}
